import { Inject, Injectable } from '@angular/core';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig, DataUserDataService, DisplayDataFactoryService } from '@ddt-web/data-service';
import { StoreService } from '@ddt-web/store-service';
import { SharedQueryParamKey, UrlService } from '@ddt-web/url';
import * as castArray from 'lodash/castArray';
import * as isEmpty from 'lodash/isEmpty';
import { combineLatest } from 'rxjs';
import { distinctUntilKeyChanged, filter, first, map, switchMap, take } from 'rxjs/operators';

import { PasPositionDetailsLoadedProcessingService } from '../pas-setup-data-workers';
import { PasDataLoaderService } from '../pas-setup-data-workers/services/pas-data-loader.service';
import { EngagementsUtil } from '../utils/engagements.util';
import { EngagementResolverWorker } from './engagement-resolver-worker';


@Injectable()
export class ApbEngagementResolverWorker extends EngagementResolverWorker {
	@InjectConfig(PaConfigKey.ENABLE_FUND_STATS) private readonly isFundStatsEnabled: boolean = false;

	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig,
		public storeService: StoreService,
		protected pasDataLoaderService: PasDataLoaderService,
		protected displayDataService: DisplayDataFactoryService,
		protected dataUserDataService: DataUserDataService,
		protected pasPositionDetailsLoadedProcessingService: PasPositionDetailsLoadedProcessingService,
		private urlService: UrlService
	) {
		super(storeConfig, pasDataLoaderService, displayDataService, storeService, dataUserDataService);
	}

	public handle(queryParams: any): any {
		if (!isEmpty(castArray(queryParams?.engagement ?? []))) {
			this.setupEngagement(queryParams);
		}
	}

	public setupEngagement(queryParams): void {
		combineLatest([
			this.storeService.selectAll(this.storeConfig.engagementPortfolios),
			this.storeService.selectAll(this.storeConfig.modelsConfig),
			this.storeService.selectAll(this.storeConfig.associatedPortfolios)
		]).pipe(
			first(
				([engagementPortfolios, modelsConfig, associatedPortfolios]: any) =>
					modelsConfig && !modelsConfig.isLoading && engagementPortfolios && !engagementPortfolios.isLoading && engagementPortfolios.entities
			)
		).subscribe(([engagementPortfolios, modelsConfig, associatedPortfolios]) => {
			this.loadEngagementData(queryParams, engagementPortfolios, modelsConfig, associatedPortfolios);
		});
	}

	protected setDataLoader(engagementId, engagementPortfolios, modelsConfig, associatedPortfolios): void {
		const engagementPortfolio = engagementPortfolios.entities[engagementId];
		let ids;
		const engagementConstituentPortfolios = engagementPortfolio.children.map(child => EngagementsUtil.getEngagementPortfolioId(child, modelsConfig?.entities));
		this.storeService.upsertMany(this.storeConfig.associatedPortfolios, engagementConstituentPortfolios);
		// No security enrichment needed for engagements as we already have globalId coming with get-all call
		this.storeService.selectAll(this.storeConfig.associatedPortfolios)
			.pipe(
				take(1),
				switchMap(associatedPortfolios =>
					this.urlService.queryParams$.pipe(
						distinctUntilKeyChanged(SharedQueryParamKey.ENGAGEMENT), // do not trigger if query params is updated but the engagement param is the same
						map(queryParams => [associatedPortfolios, queryParams])
					)
				),
				// required so that we only trigger the subscription when we're on an engagement url
				// otherwise we also trigger on new analysis which will then incorrectly display the engagement analysis
				filter(([associatedPortfolios, queryParams]) => !!queryParams.engagement)
			)
			.subscribe(([associatedPortfolios, queryParams]) => {
				ids = this.pasDataLoaderService.populateStoreForEngagement(
					engagementPortfolio,
					associatedPortfolios,
					modelsConfig?.entities ?? {},
					null,
					null,
					{ currency: 'USD', identifierToBeUsedAsWweApiParamId: 'identifier' }
				);
				if (this.isFundStatsEnabled) {
					this.pasDataLoaderService.populateStoresForPositions(
						ids,
						true,
						{ currency: 'USD', identifierToBeUsedAsWweApiParamId: 'identifier' },
						null,
						[this.pasPositionDetailsLoadedProcessingService]
					);
				} else {
					this.pasDataLoaderService.populateStoresForPositions(
						ids,
						true,
						{ currency: 'USD', identifierToBeUsedAsWweApiParamId: 'identifier' }
					);
				}

				this.displayDataService.setDefaultIds(ids);
			});
	}
}


